import { QuestionnaireCategoryItemsItemOneOf as Question, Questionnaire } from '@/api/generated-catch-backend/model';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { isQuestion } from '../schemas/questionnaire-schema';
import { DeselectDisplay } from './deselect-display/deselect-display';
import { ErrorDisplay } from './error-display/error-display';
import { QuestionnaireCard } from './questionnaire-card/questionnaire-card';
import { Header } from './questionnaire-header/header';
import { QuestionnaireWrapper } from './questionnaire-wrapper';
import { SkipDisplay } from './skip-display/skip-display';
import { NextButton } from './survey-item/components/next-button/button';
import { InterstitialSkeleton } from './survey-item/components/interstitial/interstitial-skeleton';
import { SurveyItem } from './survey-item/survey-item';

// Context and hooks
import { QuestionnaireProvider, useQuestionnaireContext } from '../context/QuestionnaireContext';
import { useQuestionnaireForm } from '../hooks/use-questionnaire-form';
import { useQuestionnaireLoading } from '../hooks/use-questionnaire-loading';
import { useQuestionnaireNavigation } from '../hooks/use-questionnaire-navigation';
import { useQuestionnaireSession } from '../hooks/use-questionnaire-session';
import { extractLottieUrls, usePrefetchLotties } from '../utils/lottie-queries';
import { ThreeDotLoader } from './loader/loader';
import { OnNext } from './types';

export interface QuestionnaireAnalyticsHandlers {
  onPageViewed?: (slug: string) => void;
  onAnswerSubmitted?: (slug: string, answerValue: string) => void;
}

interface QuestionnaireContentProps {
  questionnaire?: Questionnaire;
  onComplete?: () => void;
  analyticsHandlers?: QuestionnaireAnalyticsHandlers;
}

function QuestionnaireContent({ questionnaire: providedQuestionnaire, onComplete, analyticsHandlers }: QuestionnaireContentProps) {
  const { state, dispatch } = useQuestionnaireContext();
  const questionTitleRef = useRef<HTMLDivElement>(null);

  // Initialize hooks
  useQuestionnaireLoading(providedQuestionnaire);
  const {
    selectedQuestionnaire,
    questions,
    questionToCategory,
    getOrCreateResponse,
    responseWithAnswers,
    resetSession,
  } = useQuestionnaireSession(providedQuestionnaire);

  const {
    form,
    resetField,
    handleNext: formHandleNext,
    handleFormSubmit,
    getValues,
    FormProvider,
    isQuestionAnswered,
    loadFormDataFromAnswers,
  } = useQuestionnaireForm(
    getOrCreateResponse,
    async () => {
      await resetSession();
    },
    onComplete,
    analyticsHandlers,
  );

  const { currentQuestionIndex, questionnaireWrapperRef, handleBack, isNavigationBusy } = useQuestionnaireNavigation(
    resetField,
    isQuestionAnswered,
  );

  // Prefetch Lottie animations
  const prefetchLotties = usePrefetchLotties();

  // Current question and answer
  const currentQuestion = questions[currentQuestionIndex];

  // Lock body scroll to prevent iOS Safari pull-to-refresh + set accurate viewport height
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.width = '100%';
    body.style.height = '100%';

    const setAppHeight = () => {
      document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
    };
    setAppHeight();
    window.addEventListener('resize', setAppHeight);

    return () => {
      html.style.overflow = '';
      body.style.overflow = '';
      body.style.position = '';
      body.style.width = '';
      body.style.height = '';
      document.documentElement.style.removeProperty('--app-height');
      window.removeEventListener('resize', setAppHeight);
    };
  }, []);

  // Track page viewed when current question changes
  const analyticsHandlersRef = useRef(analyticsHandlers);
  analyticsHandlersRef.current = analyticsHandlers;
  useEffect(() => {
    if (currentQuestion?.slug && analyticsHandlersRef.current?.onPageViewed) {
      analyticsHandlersRef.current.onPageViewed(currentQuestion.slug);
    }
  }, [currentQuestion?.slug]);

  // Load form data from backend when responseWithAnswers first becomes available.
  // Only runs once per session restore — not re-triggered by question updates.
  const hasLoadedFormData = useRef(false);
  useEffect(() => {
    if (responseWithAnswers?.items?.length && !hasLoadedFormData.current) {
      hasLoadedFormData.current = true;
      loadFormDataFromAnswers(responseWithAnswers.items, questions);
    }
  }, [responseWithAnswers, loadFormDataFromAnswers, questions]);

  useEffect(() => {
    form.clearErrors();
  }, [currentQuestionIndex, form]);

  const memoizedLottieUrls = useMemo(() => extractLottieUrls(state.questions), [state.questions]);
  useEffect(() => {
    if (questions.length > 0) {
      if (memoizedLottieUrls.length > 0) {
        prefetchLotties(memoizedLottieUrls);
      }
    }
  }, [questions, prefetchLotties, memoizedLottieUrls]);

  // Combine navigation and form handling
  const handleNext: OnNext = useCallback(
    (args) => {
      formHandleNext(args);
    },
    [formHandleNext],
  );

  // Listen for email-captured event from email-only questions
  // TODO: Refactor questions to optionally render progress and wrapper so we don't have to do this
  useEffect(() => {
    const handleEmailCaptured = () => {
      handleNext({ skipQuestion: false });
    };

    document.addEventListener('email-captured', handleEmailCaptured);
    return () => {
      document.removeEventListener('email-captured', handleEmailCaptured);
    };
  }, [handleNext]);

  // Category progress calculation
  const currentCategoryIndex = questionToCategory.get(currentQuestionIndex) || 0;
  const totalCategories = selectedQuestionnaire?.categories.length || 0;
  const questionsOnly = questions.filter(isQuestion);

  // Calculate the index within questions-only array
  const currentQuestionOnlyIndex = questions.slice(0, currentQuestionIndex + 1).filter(isQuestion).length - 1;

  if (!selectedQuestionnaire || questions.length === 0) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-black">
        <ThreeDotLoader />
      </div>
    );
  }

  // Wait for session restore before rendering to prevent flashing the wrong question.
  // Exception: if the current item is a loader interstitial, render it immediately
  // since it serves as a natural loading screen.
  const currentItemIsLoader =
    currentQuestion?.questionnaireItemKind === 'interstitial' &&
    'type' in currentQuestion &&
    currentQuestion.type === 'loader';

  if (!state.hasRestoredSession && !currentItemIsLoader) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-black">
        <ThreeDotLoader />
      </div>
    );
  }

  // Handle interstitial pages - render full screen without form
  if (currentQuestion && currentQuestion.questionnaireItemKind === 'interstitial') {
    // Check if skeleton indicates next item is also an interstitial
    const showInterstitialSkeleton = state.skeletonQuestion && [
      'intro', 'outro', 'card-stack-interstitial', 'image-interstitial',
      'video-drawer-interstitial', 'loader', 'section-divider-interstitial',
      'animated-section-divider'
    ].includes(state.skeletonQuestion.questionType);

    return (
      <QuestionnaireWrapper ref={questionnaireWrapperRef}>
        <div className="h-screen w-full">
          <Header
            currentCategoryIndex={currentCategoryIndex}
            categoryName={selectedQuestionnaire.categories[currentCategoryIndex].name}
            totalCategories={totalCategories}
            currentQuestionIndex={currentQuestionIndex}
            currentQuestionOnlyIndex={currentQuestionOnlyIndex}
            questions={questionsOnly}
            questionToCategory={questionToCategory}
            questionnaire={selectedQuestionnaire}
            onBack={handleBack}
            isBackDisabled={isNavigationBusy}
            isInterstitial={true}
          />
          <AnimatePresence mode="wait">
            {showInterstitialSkeleton ? (
              <motion.div
                key="interstitial-skeleton"
                initial={{ x: state.navigationDirection === 'forward' ? 50 : -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: state.navigationDirection === 'forward' ? -50 : 50, opacity: 0 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="h-full w-full"
              >
                <InterstitialSkeleton />
              </motion.div>
            ) : (
              <motion.div
                key={currentQuestion.slug}
                initial={{ x: state.navigationDirection === 'forward' ? 50 : -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: state.navigationDirection === 'forward' ? -50 : 50, opacity: 0 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="h-full w-full"
              >
                <SurveyItem
                  surveyItem={currentQuestion}
                  onWhyWeAskExpand={() => dispatch({ type: 'SET_WHY_WE_ASK_EXPANDED', payload: true })}
                  hideWhyWeAsk={state.isWhyWeAskExpanded}
                  onNext={handleNext}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </QuestionnaireWrapper>
    );
  }

  // Handle email-only questions - render full screen with form context
  if (
    currentQuestion &&
    currentQuestion.questionnaireItemKind === 'question' &&
    currentQuestion.questionType === 'email-only'
  ) {
    return (
      <FormProvider {...form}>
        <form onSubmit={handleFormSubmit}>
          <QuestionnaireWrapper ref={questionnaireWrapperRef}>
            <div className="h-screen w-full">
              <SurveyItem
                key={currentQuestion.slug}
                surveyItem={currentQuestion}
                onWhyWeAskExpand={() => dispatch({ type: 'SET_WHY_WE_ASK_EXPANDED', payload: true })}
                hideWhyWeAsk={state.isWhyWeAskExpanded}
                onNext={handleNext}
              />
            </div>
          </QuestionnaireWrapper>
        </form>
      </FormProvider>
    );
  }

  const currentAnswer = currentQuestion ? getValues(currentQuestion.slug) : undefined;

  return (
    <FormProvider {...form}>
      <form onSubmit={handleFormSubmit} className="fixed top-0 left-0 right-0 overflow-hidden overscroll-none" style={{ height: 'var(--app-height, 100dvh)' }}>
        <QuestionnaireWrapper
          ref={questionnaireWrapperRef}
          className="questionnaire-wrapper h-full overflow-hidden"
        >
          {/* Header - fixed height */}
          <div className="flex-shrink-0">
            <Header
              currentCategoryIndex={currentCategoryIndex}
              categoryName={selectedQuestionnaire.categories[currentCategoryIndex].name}
              totalCategories={totalCategories}
              currentQuestionIndex={currentQuestionIndex}
              currentQuestionOnlyIndex={currentQuestionOnlyIndex}
              questions={questionsOnly}
              questionToCategory={questionToCategory}
              questionnaire={selectedQuestionnaire}
              onBack={handleBack}
              isBackDisabled={isNavigationBusy}
            />
          </div>

          {/* Card + Footer wrapper — single white background, no gap */}
          <div className="flex min-h-0 flex-1 w-full flex-col overflow-hidden rounded-t-[28px] bg-white shadow-[0px_10px_100px_0px_rgba(85,94,104,0.1)] backdrop-blur-[10px] backdrop-filter">
            <QuestionnaireCard
              isWhyWeAskExpanded={state.isWhyWeAskExpanded}
              setIsWhyWeAskExpanded={(expanded: boolean) =>
                dispatch({ type: 'SET_WHY_WE_ASK_EXPANDED', payload: expanded })
              }
              currentQuestion={currentQuestion as Question}
              handleNext={handleNext}
              questionTitleRef={questionTitleRef}
              skeletonQuestion={state.skeletonQuestion}
              navigationDirection={state.navigationDirection}
            />

            {/* Footer - normal flow, outside scroll area */}
            {!state.isWhyWeAskExpanded && (
              <div className="sticky-bottom-controls w-full pt-2 pb-4">
                <div className="controls-container">
                  <div className="controls-overlay">
                    <ErrorDisplay />
                    <DeselectDisplay />
                    <SkipDisplay onSkip={() => handleNext({ skipQuestion: true })} />
                  </div>
                </div>

                <div className="btn-primary-container">
                  <NextButton onNext={() => handleNext({ skipQuestion: false })} />
                </div>
              </div>
            )}
          </div>
        </QuestionnaireWrapper>
      </form>
    </FormProvider>
  );
}

interface QuestionnaireV2RefPageProps {
  questionnaire?: Questionnaire;
  onComplete?: () => void;
  analyticsHandlers?: QuestionnaireAnalyticsHandlers;
}

export default function QuestionnaireV2RefPage({ questionnaire, onComplete, analyticsHandlers }: QuestionnaireV2RefPageProps) {
  return (
    <QuestionnaireProvider>
      <QuestionnaireContent questionnaire={questionnaire} onComplete={onComplete} analyticsHandlers={analyticsHandlers} />
    </QuestionnaireProvider>
  );
}
